import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormInstanceFunctions, Input, InputNumber, MessagePlugin, Select, Switch } from 'tdesign-react';

import { MissionGoal } from '@/components/icon';
import { AppData } from '@/editor/flowEditor/control/types';
import { GoalType, IMission } from '@/interface';
import { IGamePlay } from '@/interface/gamePlay';

import ItemCardContainer, { CardData } from '../customBranch/Branch/numberSystem/cards';
import { defaultOptions, IOption, SelectType } from './config';
import styles from './index.module.less';

export interface IProps {
  value: IMission['missionGoal'];
  form: FormInstanceFunctions;
  disabled?: boolean;
  cparams: {
    shareInfo: AppData<Partial<IGamePlay>>['data']['shareInfo'];
  }
  onChange: (key: any) => void;
}

const CustomMissionGoal: React.FC<IProps> = (props) => {
  const { onChange, value, disabled = false, cparams } = props;
  const { shareInfo } = cparams;

  const [select, setSelect] = useState(defaultOptions[0]);
  const [round, setRound] = useState(0);

  useEffect(() => {
    setSelect(memoSelectValue);
    setRound(value?.chatRound?.round);
  }, [value]);

  const gamePlay = useMemo(() => ({
    switchGroup: shareInfo?.scene?.gamePlay?.switchGroup,
    numSys: shareInfo?.scene?.gamePlay?.numSys,
  }), [shareInfo]);

  const selectOptions = useMemo(() => defaultOptions?.map((e) => {
    let temp = { ...e };
    if (e?.switchGroupKey) {
      if (!gamePlay?.switchGroup?.[e?.switchGroupKey]) {
        temp = { ...temp, cantChoose: true };
      }
    }
    return temp;
  }), [gamePlay?.switchGroup, defaultOptions]);

  const handleSelectChange = useCallback((select: IOption) => {
    const cantChoose = selectOptions?.find(e => e.value === select.value)?.cantChoose;
    if (cantChoose) {
      MessagePlugin.warning('请先去【特殊玩法】中开启该模式');
      return;
    }
    setSelect(select);
    let postData = {};
    const { type } = select;
    if (type === SelectType.countDown) {
      postData = { [type]: { isOpen: true } };
    }
    onChange({ ...postData, goalType: select.value });
  }, []);

  const memoSelectValue = useMemo(
    () => defaultOptions.filter(e => e.value === value?.goalType)?.[0] || defaultOptions[0],
    [defaultOptions, value],
  );

  const handleSwitchChange = useCallback((value: boolean) => {
    let postData = {};
    const { type } = select;
    if (type === SelectType.countDown) {
      postData = { [type]: { isOpen: value } };
    }
    onChange({ ...postData, goalType: select.value });
  }, [select]);

  const handleInputChange = useCallback((value: string | number) => {
    let postData = {};
    const { type } = select;
    if (type === SelectType.assistantMessageGoal) {
      postData = { [type]: { text: value } };
    }
    if (type === SelectType.userMessageGoal) {
      postData = { [type]: { text: value } };
    }
    if (type === SelectType.chatRound) {
      postData = { [type]: { round: value } };
    }

    onChange({ ...postData, goalType: select.value });
  }, [select, round]);

  const memoStringValue = useMemo(() => {
    let showValue = undefined;
    const { type } = select;
    if (type === SelectType.userMessageGoal) {
      showValue = value?.userMessageGoal?.text;
    }
    if (type === SelectType.assistantMessageGoal) {
      showValue = value?.assistantMessageGoal?.text;
    }

    return showValue;
  }, [select, value]);


  const errorMsg = useMemo(() => {
    if (value?.goalType === GoalType.GOAL_TYPE_COUNTDOWN && !gamePlay?.switchGroup?.countDown && value?.countDown?.isOpen) {
      return '幕【AI玩法】中已关闭【倒计时】玩法，请更换为其他触发条件';
    }

    if (value?.goalType === GoalType.GOAL_TYPE_NUM_SYS && !gamePlay?.switchGroup?.numSys) {
      return '幕【AI玩法】中已关闭【数值系统】玩法，请更换为其他触发条件';
    }
    return undefined;
  }, [value, gamePlay?.switchGroup]);

  const handleActivationCond = (val: CardData[]) => {
    onChange({ ...value, numSysGoal: { activationCond: val } });
  };

  return (
    <div className={styles.containerWrap}>
      <div className={classNames('custom-form-group', styles.container)} >
        <div className={styles.left}>
          <Select
            prefixIcon={
              <div className={styles.noPaddingIcon}>
                <MissionGoal />
              </div>
            }
            disabled={disabled}
            value={select}
            onChange={val => handleSelectChange(val as IOption)}
            style={{ width: 120 }}
            valueType='object'
            options={selectOptions}
          />
        </div>
        <div className={styles.right}>
          {
            select.render === 'string' ? (
              <div className={styles.input}>
                <Input
                  disabled={disabled}
                  align="left"
                  status="default"
                  type="text"
                  value={memoStringValue}
                  onChange={value => handleInputChange(value)}
                  className={styles.input}
                />
              </div>
            ) : null
          }
          {
            select.render === 'switch' ? (
              <div className={styles.input}>
                <Switch
                  size="medium"
                  label={['开', '关']}
                  value={value?.countDown?.isOpen}
                  onChange={handleSwitchChange}
                />
              </div>
            ) : null
          }
          {
            select.render === 'number'
              ? (
                <div>
                  <InputNumber
                    disabled={disabled}
                    align="left"
                    status="default"
                    decimalPlaces={0}
                    step={1}
                    {...select?.uiOptions}
                    value={round}
                    onChange={value => handleInputChange(value)}
                    className={styles.input}
                    allowInputOverLimit={false}
                  />
                </div>
              ) : null
          }
          {
            select.render === 'object'
              ? (
                <div className={styles.flexWrap}>
                  <ItemCardContainer
                    label=''
                    fileName={'activationCond'}
                    disabled={disabled}
                    value={value?.numSysGoal?.activationCond || []}
                    leftStyle={{
                      width: 0,
                    }}
                    onChange={val => handleActivationCond(val)}
                  />
                </div>
              ) : null
          }
        </div>
      </div>
      {value?.goalType === GoalType.GOAL_TYPE_COUNTDOWN ? <div className={styles.tips}>倒计时结束即游戏失败，自动返回故事线</div> : null}

      {errorMsg ? <div className={styles.error}>{errorMsg}</div> : null}

    </div>
  );
};

export default CustomMissionGoal;
